import { Link } from 'react-router-dom';
import { mainSiteUrl, categories } from '@/data/categories';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://yoybuy.life';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Полное руководство по Yoybuy Spreadsheet 2026: Как находить лучшие товары на Taobao, Weidian и 1688',
  description:
    'Узнайте, как использовать Yoybuy Spreadsheet для безопасных и выгодных покупок из Китая с доставкой в Россию. Пошаговое руководство, советы экспертов и проверенные стратегии.',
  author: {
    '@type': 'Organization',
    name: 'Yoybuy Spreadsheet',
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Yoybuy Spreadsheet',
    url: SITE_URL,
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  inLanguage: 'ru',
  url: `${SITE_URL}/yoybuy-spreadsheet-guide`,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/yoybuy-spreadsheet-guide`,
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Главная',
      item: SITE_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Полное руководство по Yoybuy Spreadsheet',
      item: `${SITE_URL}/yoybuy-spreadsheet-guide`,
    },
  ],
};

export default function ArticlePage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <main>
        <nav className="sticky top-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between h-14">
              <Link to="/" className="flex items-center gap-2 group">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className="ri-table-line text-sm"></i>
                </div>
                <span className="font-heading text-base font-semibold text-foreground-900 whitespace-nowrap">
                  Yoybuy Spreadsheet
                </span>
              </Link>
              <div className="flex items-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer hidden sm:inline-flex"
                >
                  Yoybuy Sheet
                </a>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700 font-medium">Полное руководство по Yoybuy Spreadsheet</span>
          </nav>

          <article>
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700">
                  Руководство
                </span>
                <span className="text-xs text-foreground-500">Обновлено: 05.07.2026</span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line"></i> 12 мин чтения
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Полное руководство по Yoybuy Spreadsheet 2026: Как находить лучшие товары на Taobao, Weidian и 1688
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Узнайте, как использовать <strong>Yoybuy Spreadsheet</strong> для безопасных и выгодных покупок из Китая с доставкой в Россию. Пошаговое руководство, советы экспертов и проверенные стратегии поиска лучших товаров.
              </p>
            </header>

            <div className="prose-custom space-y-6">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Что такое Yoybuy Spreadsheet и зачем он нужен
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  <strong>Yoybuy Spreadsheet</strong> — это специализированные электронные таблицы, созданные опытными байерами 
                  для упрощения процесса покупок на китайских маркетплейсах. В отличие от обычного поиска на 
                  <strong> Taobao</strong>, <strong>Weidian</strong> и <strong>1688</strong>, где интерфейс полностью 
                  на китайском языке, а качество товаров часто непредсказуемо, Yoybuy Spreadsheet предоставляет 
                  уже проверенные и структурированные данные.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  Каждая таблица Yoybuy Spreadsheet содержит подробную информацию о товаре: прямую ссылку на 
                  страницу продавца, актуальную цену в юанях и примерную стоимость в рублях, QC-фотографии 
                  (контроль качества), размерную сетку с рекомендациями, рейтинг продавца и отзывы реальных 
                  покупателей. Это позволяет минимизировать риски и экономить десятки часов на поиске.
                </p>
              </section>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-8">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                  Ключевые преимущества Yoybuy Spreadsheet
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-checkbox-circle-line', text: 'Ручная проверка каждого товара' },
                    { icon: 'ri-refresh-line', text: 'Еженедельное обновление данных' },
                    { icon: 'ri-image-line', text: 'QC-фотографии высокого качества' },
                    { icon: 'ri-shield-check-line', text: 'Только проверенные продавцы' },
                    { icon: 'ri-price-tag-line', text: 'Актуальные цены в юанях и рублях' },
                    { icon: 'ri-global-line', text: 'Оптимизировано для доставки в Россию' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 mt-0.5">
                        <i className={`${item.icon} text-sm`}></i>
                      </span>
                      <span className="text-sm text-foreground-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Как использовать Yoybuy Spreadsheet: пошаговая инструкция
                </h2>

                <div className="space-y-5">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold">1</div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-800 mb-2">Выбор категории товаров</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Перейдите на <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">Yoybuy Sheet</a> и выберите интересующую вас категорию из 14 доступных: 
                        обувь, худи и свитера, футболки, куртки, штаны и шорты, головные уборы, комплекты, 
                        нижнее бельё, джерси, аксессуары, сумки, ремни, парфюмерия и прочее. 
                        Каждая категория содержит от 20 до 200 проверенных товаров.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold">2</div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-800 mb-2">Анализ таблицы товаров</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Изучите таблицу: обратите внимание на цену, QC-фотографии, размерную сетку и рейтинг 
                        продавца. В таблицах Yoybuy Spreadsheet все данные структурированы и легко читаются. 
                        QC-фотографии показывают реальное качество товара, а не отретушированные изображения 
                        из карточки товара.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold">3</div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-800 mb-2">Копирование ссылки и оформление заказа</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Скопируйте ссылку на выбранный товар из таблицы. Затем откройте вашего агента по доставке — 
                        <strong> Yoybuy</strong>, <strong>Superbuy</strong>, <strong>Kakobuy</strong> или другого — 
                        вставьте ссылку в форму заказа, выберите размер и цвет (если применимо), оплатите заказ. 
                        Товар поступит на склад агента в Китае в течение 3-7 дней.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold">4</div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-800 mb-2">QC-проверка на складе</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Когда товар поступает на склад агента, вы получаете дополнительные QC-фотографии. 
                        Сравните их с фотографиями из Yoybuy Spreadsheet. Если качество соответствует ожиданиям, 
                        подтвердите отправку. Если есть проблемы, вы можете запросить возврат или обмен.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold">5</div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-800 mb-2">Доставка в Россию</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Выберите способ международной доставки. Для России доступны несколько вариантов: 
                        авиадоставка (7-14 дней), экспресс-доставка (5-10 дней) и экономичная доставка (14-28 дней). 
                        Отслеживайте посылку по трек-номеру. Среднее время доставки в Россию составляет 2-4 недели 
                        в зависимости от выбранного способа.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Категории товаров в Yoybuy Spreadsheet
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  Yoybuy Spreadsheet охватывает 14 основных категорий товаров, 
                  каждая из которых тщательно проработана:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {categories.map((cat, idx) => (
                    <a
                      key={idx}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-md bg-background-50 text-primary-500">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </section>

              <section className="mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Советы экспертов для успешных покупок
                </h2>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-accent-50 border border-accent-200/50">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">
                      <i className="ri-lightbulb-line mr-1 text-accent-500"></i>
                      Всегда проверяйте QC-фотографии
                    </h3>
                    <p className="text-sm text-foreground-600">
                      QC-фотографии в Yoybuy Spreadsheet — это реальные снимки товара со склада. 
                      Они показывают фактическое качество, которое может отличаться от фотографий продавца. 
                      Всегда сравнивайте их перед заказом.
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-accent-50 border border-accent-200/50">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">
                      <i className="ri-lightbulb-line mr-1 text-accent-500"></i>
                      Используйте размерные сетки
                    </h3>
                    <p className="text-sm text-foreground-600">
                      Китайские размеры часто отличаются от российских. Yoybuy Spreadsheet содержит 
                      подробные размерные сетки с рекомендациями для каждого товара. 
                      Всегда сверяйтесь с ними перед выбором размера.
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-accent-50 border border-accent-200/50">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">
                      <i className="ri-lightbulb-line mr-1 text-accent-500"></i>
                      Следите за обновлениями таблиц
                    </h3>
                    <p className="text-sm text-foreground-600">
                      Таблицы Yoybuy Spreadsheet обновляются каждую неделю. Новые находки, 
                      актуальные цены и свежие QC-фото появляются регулярно. 
                      Подпишитесь на обновления, чтобы не пропустить лучшие предложения.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Часто задаваемые вопросы о Yoybuy Spreadsheet
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-semibold text-foreground-800 mb-2">
                      Безопасно ли покупать через Yoybuy Spreadsheet?
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Да, абсолютно безопасно. Все товары в таблицах проходят ручную проверку. 
                      Мы проверяем репутацию продавцов, качество товаров по QC-фотографиям 
                      и актуальность цен. Однако помните, что Yoybuy Spreadsheet — это инструмент 
                      для поиска товаров, а саму покупку вы совершаете через агента по доставке.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-800 mb-2">
                      Сколько стоит доставка из Китая в Россию?
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Стоимость доставки зависит от веса посылки, выбранного способа доставки 
                      и вашего региона в России. В среднем, доставка одной пары обуви обходится 
                      в 1500-3000 рублей, худи или футболки — 800-1500 рублей. 
                      Точную стоимость рассчитывает ваш агент при оформлении заказа.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-800 mb-2">
                      Могу ли я вернуть товар, если он не подошёл?
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Да, большинство агентов поддерживают возврат товаров на складе в Китае. 
                      Если QC-фотографии показали дефект или товар не соответствует описанию, 
                      вы можете запросить возврат до отправки в Россию. После отправки возврат 
                      обычно невозможен, поэтому внимательно проверяйте QC-фото.
                    </p>
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70 text-center">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                Готовы начать покупки с Yoybuy Spreadsheet?
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                Перейдите на основной сайт и получите доступ к сотням проверенных товаров 
                с актуальными ссылками, ценами и QC-фотографиями.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap w-full sm:w-auto px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-shopping-bag-3-line mr-2"></i>
                  Yoybuy Sheet
                </a>
                <Link
                  to="/"
                  className="whitespace-nowrap w-full sm:w-auto px-6 py-3 text-sm font-semibold rounded-full border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer text-center"
                >
                  <i className="ri-home-line mr-2"></i>
                  На главную
                </Link>
              </div>
            </div>
          </article>
        </div>

        <footer className="bg-accent-500 mt-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-50 text-accent-500">
                  <i className="ri-table-line text-sm"></i>
                </div>
                <span className="font-heading text-base font-semibold text-background-50 whitespace-nowrap">
                  Yoybuy Spreadsheet
                </span>
              </Link>
              <div className="flex items-center gap-4 text-xs text-background-50/60">
                <Link to="/" className="hover:text-background-50 transition-colors">Главная</Link>
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="hover:text-background-50 transition-colors cursor-pointer">Основной сайт</a>
              </div>
              <p className="text-xs text-background-50/60">
                © 2026 Yoybuy Spreadsheet. Все права защищены.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
}